import { Error } from '@progress/kendo-react-labels';
import { Box, Button, FormControl, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import DropdownField from '../../Shared/Forms/KendoFields/DropdownField';
import TextFieldInput from '../../Shared/Forms/KendoFields/TextFieldInput';
import NumberFieldInput from '../../Shared/Forms/KendoFields/NumberFieldInput';
import { OverlayLoading } from '../helper/SimulatorLoading';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetBenefitLoading,
  loadEmployeeBenefit,
  resetBenefitData,
  updateEmployeeBenefit,
} from '../slice/fvSimulatorSlice';
import { selectEmployeeBenefitLoading, selectEmployeeBenefitData, selectWelcomeKitLoading, selectWelcomeKitData } from '../slice/selector';
import { showNotification } from '../../Notifications/notificationSlice';
import { useLocation } from 'react-router-dom';
import { selectUserDetails } from '../../Users/selectors';
import { useParams } from 'react-router-dom';
import { useReducer, useEffect } from 'react';
import { getUser } from '../../Users/usersSlice';
import { useRef } from 'react';
import ToggleInput from '../../Shared/Forms/KendoFields/ToggleField';
import { loadWelcomeKit, resetWelcomeKitData, resetWelcomeKitLoading, updateWelcomeKit } from '../slice/shopifySimulatorSlice';


const welcomeKitField = {
  type: 'toggle',
  name: 'welcomeKitEnable',
  label: 'User Claimed Welcome Kit',
}


const welcomeKitReducer = (state, action) => ({
  ...state,
  // if it is a function
  ...(typeof action === 'function' ? action(state) : action),
});

/* default values */
const defaultState = {
  welcomeKitValue: false,
};

function WelcomeKitSimulator() {
  const dispatch = useDispatch();
  const params = useParams();

  const welcomeKitLoading = useSelector(selectWelcomeKitLoading);
  const welcomeKitSliceData = useSelector(selectWelcomeKitData);

  const [welcomeKitState, setWelcomeKitState] = useReducer(welcomeKitReducer, defaultState, (init) => {
    if (welcomeKitSliceData) {
      return {
        /* default to false if GET api didn't return any data */
        welcomeKitValue: welcomeKitSliceData ?? false,
      };
    }
    return init;
  });

  /* need to get user's "_id" not just email/userId */
  const { userId } = params;
  const userDetails = useSelector(selectUserDetails);
  const isMounted = useRef(false);

  useEffect(() => {
    const getUserData = async () => {
      dispatch(getUser(userId, true));
    };

    getUserData();

    if (welcomeKitSliceData !== null || welcomeKitSliceData !== undefined) {
      setWelcomeKitState({
        welcomeKitValue: welcomeKitSliceData ?? false,
      });
    }
    isMounted.current = true;
  }, [userId, welcomeKitSliceData]);
  /* END - getting userDetails for _id */

  const { welcomeKitValue } = welcomeKitState;
  const [error, setError] = useState({});

  // const accountId = localStorage.getItem('accountId');
  const tenantId = localStorage.getItem('tenantId');

  const handleWelcomeKitChange = (fieldName, fieldValue) => {
    /* Clear Error */
    let clearedErr = { ...error };
    delete clearedErr['welcomeKitValue'];
    setError({ ...clearedErr });

    setWelcomeKitState({ welcomeKitValue: fieldValue });
  };

  useEffect(() => {
    const params = {
      tenantId,
      /**
       * Pass the email as userId if userDetails is not yet available
       * where: userId => email address
       * email address also works with GET but will received no data property yet.
       * On next render it will use the proper userDetails?._id and will have data
       */
      userId: userDetails?._id,
    };

    if (userDetails?._id) {
      dispatch(loadWelcomeKit(params));
    }

    return () => {
      dispatch(resetWelcomeKitLoading());
      dispatch(resetWelcomeKitData());
    };
  }, [userId, userDetails?._id]);

  useEffect(() => {
    if (welcomeKitLoading === 'fulfilled' || welcomeKitLoading === 'rejected') {
      dispatch(resetWelcomeKitLoading());
    }
  }, [welcomeKitLoading]);

  /* SUBMIT SIMULATION REQUEST */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasError = validateInputs();

    if (hasError) {
      return null;
    }

    const userIdPayload = userDetails?._id ?? null;
    if (!userIdPayload) return;

    const payload = {
      // tenantID: 'healthcheck-consumer', // for local DEV only
      tenantId,
      // userId,  => using _id instead
      userId: userIdPayload,
      formData: {
        value: welcomeKitValue,
      },
    };

    try {
      const result = await dispatch(updateWelcomeKit(payload));

      if (result.error) {
        dispatch(showNotification(`Failed to execute simulator <br />Error: ${result.error.message}`, 'error'));
        dispatch(resetWelcomeKitLoading());
        return;
      }

      if (result.payload[1]?.status >= 400) {
        dispatch(showNotification(`Failed to execute simulator <br />Error: ${result?.payload[1]?.message}`, 'error'));
        dispatch(resetWelcomeKitLoading());
        return;
      }

      if (result.payload[0]?.success) {
        dispatch(
          showNotification(`${result?.payload[0]?.message} <br />User Welcome Kit updated successfully`, 'success')
        );
        return;
      }
    } catch (error) {
      dispatch(showNotification(`Failed to execute simulator <br />Error: ${error.message}`, 'error'));
      dispatch(resetWelcomeKitLoading());
    }
  };

  const validateInputs = () => {
    let hasError = false;
    if (welcomeKitValue === undefined || welcomeKitField === null) {
      setError((err) => ({
        ...err,
        welcomeKitValue: {
          message: 'User Claimed Welcome Kit cannot be undefined',
        },
      }));
      hasError = true;
    }
    return hasError;
  };

  return (
    <Box width={'100%'} height={'100%'}>
      {(welcomeKitLoading === 'pending' || !isMounted.current || welcomeKitSliceData === null) && <OverlayLoading />}
      <Box
        display={'grid'}
        gridTemplateColumns={'repeat(2, minmax(0, 1fr))'}
        gridRowGap={'50px'}
        gridColumnGap={'100px'}
        marginTop={'25px'}
        marginBottom={'25px'}
      >
        <Box textAlign={'left'}>
          <ToggleInput
            field={welcomeKitField}
            value={welcomeKitValue}
            handleChangeWithParams={handleWelcomeKitChange}
          />
          {error?.welcomeKitValue && <Error>{error?.welcomeKitValue?.message}</Error>}
        </Box>
        <Box gridColumn={'2 / 3 '} display="flex" justifyContent={'center'}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={welcomeKitLoading === 'pending'}
          >
            Simulate
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default WelcomeKitSimulator;
